/**
 * Narration, as real `<p>` elements (checkbox two).
 *
 * This component is a deletion as much as an addition. Narration used to be rendered with
 * `text.replace(/\n/g, '<br>')` into `dangerouslySetInnerHTML` (`StoryContainer.jsx:19`),
 * which is two separate problems wearing one line of code:
 *
 * 1. **Typography.** `<br>` is a line break, not a paragraph. There is nothing to give
 * `margin-block` to, nothing for a screen reader to navigate by, and no way to set
 * paragraph spacing without setting line spacing with it.
 * 2. **Injection.** LLM prose is not trusted input. The DM writes what the model produces,
 * and the model writes what a player's action can steer; `dangerouslySetInnerHTML` on that
 * path is an XSS foot-gun with a story-shaped delivery mechanism.
 *
 * Both vanish by splitting on blank lines and letting React build the nodes, which is also
 * how the API encodes it — `narration` is paragraphs joined by `\n\n`.
 *
 * **U6's links are the second reason that argument matters.** The obvious way to make a name
 * tappable is to splice an `<a>` into the prose as a string, which is the `innerHTML` path
 * again wearing a different hat. Instead the paragraph arrives pre-cut into runs
 * (`paragraphs.ts`) and a marked run becomes a real `<button>` node — everything stays React
 * elements, and no offset arithmetic reaches this file.
 */
import type { Mention } from '@/api/generated';
import { toParagraphRuns, type Run } from '@/features/story/paragraphs';
import { openCodex } from '@/features/world/codex';
import { useT } from '@/i18n/context';

interface ProseProps {
  text: string;
  /**
   * U6. Omitted by the live scene, which stays plain prose while it streams: the spans only
   * exist once the chapter commits, and a link that appeared under the player's eye halfway
   * through a sentence would be worse than one that appears when the scene is finished.
   */
  mentions?: Mention[];
  /** Appended to the last paragraph — the streaming cursor's slot the streaming cursor. */
  trailing?: React.ReactNode;
  className?: string;
}

/**
 * The narration face, measure and rhythm all come from tokens (the story feed's "typography first"):
 * nothing here picks a size or a family, so the reading settings change `--measure` and
 * `--text-narration` on `:root` and this follows without being touched.
 */
export function Prose({ text, mentions, trailing, className }: ProseProps) {
  const paragraphs = toParagraphRuns(text, mentions ?? []);

  if (paragraphs.length === 0 && trailing === undefined) return null;

  return (
    <div className={className}>
      {paragraphs.map((runs, index) => (
        <p
          // Narration paragraphs have no identity beyond their position, and the array is
          // append-only while streaming — an index key is correct here rather than lazy.
          key={index}
          className="font-narration text-fg mt-[var(--space-paragraph)] first:mt-0"
          style={{ fontSize: 'var(--text-narration)', lineHeight: 'var(--leading-narration)' }}
        >
          {runs.map((run, runIndex) =>
            run.mention === null ? (
              run.text
            ) : (
              <MentionLink key={runIndex} run={run} mention={run.mention} />
            ),
          )}
          {index === paragraphs.length - 1 ? trailing : null}
        </p>
      ))}
      {paragraphs.length === 0 ? (
        <p
          className="font-narration text-fg"
          style={{ fontSize: 'var(--text-narration)', lineHeight: 'var(--leading-narration)' }}
        >
          {trailing}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A proper noun that opens its codex card.
 *
 * **A real `<button>`, not a styled `<span>` with an onClick.** `eslint-plugin-jsx-a11y` flags
 * the span, and rightly: this is the navigation, so it has to be reachable by Tab, activated
 * by Enter and Space, and announced as something that does a thing.
 *
 * The styling is inline rather than `ui/Button.tsx` for the reason the whole game UI is —
 * that primitive is for decisions, and this is a word in a sentence. A dotted underline is the
 * entire treatment: it sits under the baseline and disturbs neither `--leading-narration` nor
 * the measure, which a border or a background would.
 */
function MentionLink({ run, mention }: { run: Run; mention: Mention }) {
  const t = useT();

  return (
    <button
      type="button"
      // The words are the label a sighted reader sees; this is the one a screen reader hears,
      // and it has to say what activating it *does* — "Ferron" alone announces a button whose
      // purpose is invisible. It renders in the story's language like the rest of the chrome
      // inside `/play/:storyId`.
      aria-label={t('ui.world.mention.open', { name: run.text })}
      onClick={() => {
        openCodex({ kind: mention.kind, key: mention.key });
      }}
      className="decoration-fg-faint hover:text-accent hover:decoration-accent cursor-pointer underline decoration-dotted underline-offset-4 transition-colors"
    >
      {run.text}
    </button>
  );
}
